import React, { useState, useEffect } from 'react';
import { Delete, Edit, FileText, Lock, MoreVertical, Music, Play, Trash, Video, X } from 'lucide-react';
import { NavLink, useParams } from 'react-router-dom';
import { DashboardApi } from '../../../apis/DashboardApi';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch } from 'react-redux';
import { AxiosResponse } from 'axios';
import { shared } from '../../../reducer/SharedSlice';

interface RecipientProfile {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    status: string;
    group: string;
    temporaryUrl: string;
    media: {
        id: number;
        file_name: string;
        file_type: string;
        temporaryUrl: string;
    }[];
}

interface Media {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
    is_legacy: number;
    handleDelete?: () => void;
    handleRename?: () => void;
    handleShare?: () => void;
}


interface DeleteConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    recipientName: string;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    recipientName
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold">Delete Recipient</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <p className="text-gray-600 mb-6">
                    Are you sure you want to delete {recipientName}? This action cannot be undone.
                </p>

                <div className="flex justify-end gap-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};


interface MediaModalProps {
    media: Media | null;
    onClose: () => void;
}



const MediaModal: React.FC<MediaModalProps> = ({ media, onClose }) => {
    if (!media) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="relative bg-white rounded-lg max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="p-4">
                    {media.file_type === 'video' && (
                        <div className="aspect-video p-2 pt-5">
                            <video
                                src={media.temporaryUrl}
                                controls
                                className="w-full h-full rounded-lg"
                            >
                                Your browser does not support video playback.
                            </video>
                        </div>
                    )}

                    {media.file_type === 'image' && (
                        <div className="relative max-h-[80vh] overflow-hidden p-2 pt-5">
                            <img
                                src={media.temporaryUrl}
                                alt={media.file_name}
                                className="w-full h-full object-contain rounded-lg"
                            />
                        </div>
                    )}

                    {media.file_type === 'audio' && (
                        <div className="p-2 pt-5">
                            <audio
                                src={media.temporaryUrl}
                                controls
                                className="w-full"
                            >
                                Your browser does not support audio playback.
                            </audio>
                        </div>
                    )}

                    {media.file_type === 'document' && (
                        <div className="p-2 pt-5">
                            <iframe
                                src={`https://docs.google.com/viewer?url=${encodeURIComponent(media.temporaryUrl)}&embedded=true`}
                                className="w-full max-h-[600px] border rounded-md"
                                frameBorder="0"
                            >
                                Your browser does not support viewing documents.
                            </iframe>
                        </div>
                    )}

                    {/* {media.file_type === 'document' && (
                        <div className="p-8 ">
                            <iframe
                                src={media.temporaryUrl}
                                className="w-full h-[100px] border rounded-md"
                            // frameBorder="0"
                            >
                                Your browser does not support viewing documents.
                            </iframe>
                        </div>
                    )} */}


                    <div className="mt-4 flex justify-between items-center">
                        <h3 className="text-lg font-medium">{media.file_name}</h3>
                        {media.is_legacy === 1 && (
                            <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-500">Private</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const RecipientDetails = () => {
    const [activeTab, setActiveTab] = useState<'profile' | 'media'>('profile');
    const [recipient, setRecipient] = useState<any>(null);
    const [media, setMedia] = useState<any>(null);
    const { id } = useParams<{ id: string }>();

    const [selectedMedia, setSelectedMedia] = useState<any | null>(null);
    const [deleteMedia, setDeleteMedia] = useState<boolean>(false);

    const handleMediaClick = (media: any) => {
        setSelectedMedia(media);
    };

    const handleDeleteClick = (media: any) => {
        setDeleteMedia(true);
    };

    useEffect(() => {
        const fetchRecipient = async () => {
            try {
                const response: any = await DashboardApi.getRecipientDetails(id);
                setRecipient(response?.data?.data?.recipient);
                setMedia(response?.data?.data?.media_files)
            } catch (error) {
                console.error('Error fetching recipient:', error);
            }
        };
        fetchRecipient();
    }, [id]);

    const dispatch = useDispatch();


    const handleDeleteConfirm = async () => {
        try {
            const response: any = await DashboardApi.deleteRecipient(id);

            if (response?.data?.status === 'success') {
                DashboardApi.getMediaUsage().then(
                    (response: AxiosResponse<any>) => {
                        if (response?.data) {
                            dispatch(shared({ data: response?.data?.data }))
                            toast.success(response?.data?.message)
                            setDeleteMedia(false);
                            setTimeout(() => {
                                window.location.href = '/recipient';
                            }, 1000);
                        }
                    }
                )

            }
            // setState(prev => ({ ...prev, isDeleteModalOpen: false }));

        } catch (error) {
            console.error('Error deleting recipient:', error);
            alert('Failed to delete recipient. Please try again.');
        } finally {
        }
    };


    const renderProfileTab = () => (
        <div className="mt-8 space-y-6">
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                    <label className="text-sm text-gray-500">Name:</label>
                    <p>{recipient?.first_name} {recipient?.last_name}</p>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Email:</label>
                    <p>{recipient?.email}</p>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Phone Number:</label>
                    <p>
                        <img src="/us-flag.png" className="w-4 h-3 inline mr-2" />
                        +1 {recipient?.phone}
                    </p>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Status:</label>
                    <p>{recipient?.contact?.name}</p>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Group:</label>
                    <p>{recipient?.group?.name}</p>
                </div>
            </div>
        </div>
    );

    const renderMediaTab = () => (
        <div className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5 shadow-md p-2">
                {media?.map((item: any) => (
                    <div key={item.id} onClick={() => handleMediaClick(item)} className="relative border rounded-lg cursor-pointer ">
                        {item.file_type === 'image' && (
                                        <img
                                            src={item.temporaryUrl}
                                            alt={item.file_name}
                                            className="w-full h-32 object-cover"
                                        />
                                    )}

                                    {/* Video Preview */}
                                    {item.file_type === 'video' && (
                                        <div className="relative w-full h-32 bg-gray-100">
                                            <video
                                                src={item.temporaryUrl}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <Video className="w-8 h-8 text-gray-500" />
                                            </div>
                                        </div>
                                    )}

                                    {/* Audio Preview */}
                                    {item.file_type === 'audio' && (
                                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                                            <Music className="w-8 h-8 text-gray-500" />
                                        </div>
                                    )}

                                    {/* Document Preview */}
                                    {item.file_type === 'document' && (
                                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                                            <FileText className="w-8 h-8 text-gray-500" />
                                        </div>
                                    )}
                        {/* <div className="absolute top-2 right-2">
             <button className="p-1 bg-black/50 rounded-full">
               <MoreVertical className="w-4 h-4 text-white" />
             </button>
           </div> */}
                        <p className="mt-2 text-sm">{item.file_name}</p>
                    </div>
                ))}
            </div>
        </div>
    );

    if (!recipient) return <div>Loading...</div>;

    return (

        <div className="min-h-screen bg-white">
            <div className="flex flex-col md:flex-row">
                <Sidebar />
                <main className="flex-1 p-6 relative md:ml-64">
                    <Navbar />
                    <div className="p-6">
                        <div className="flex flex-col md:flex-row gap-6 items-start">
                            <div className="relative">
                                <img
                                    src={recipient?.temporaryUrl || '/images/profile_image.jpg'}
                                    alt={recipient?.first_name || 'Profile'}
                                    className="w-32 h-32 rounded-full object-cover"
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <h1 className="text-2xl font-semibold">{recipient.first_name} {recipient.last_name}</h1>

                                    <div className="flex justify-end gap-6">
                                        <NavLink to={`/edit-recipient/${recipient?.id}`} className="text-orange-500 flex items-center gap-2">
                                            <Edit className="w-5 h-5" />
                                            {/* Edit */}
                                        </NavLink>

                                        <div onClick={handleDeleteClick} className="text-orange-500 flex items-center gap-2 cursor-pointer">
                                            <Trash className="w-5 h-5" />
                                            {/* Delete */}
                                        </div>
                                    </div>


                                </div>
                            </div>
                        </div>

                        <div className="mt-8 border-b">
                            <div className="flex gap-8">
                                <button
                                    className={`pb-2 border-b-2 ${activeTab === 'profile' ? 'border-orange-500 text-orange-500' : 'border-transparent'}`}
                                    onClick={() => setActiveTab('profile')}
                                >
                                    Recipient's Profile
                                </button>
                                <button
                                    className={`pb-2 border-b-2 ${activeTab === 'media' ? 'border-orange-500 text-orange-500' : 'border-transparent'}`}
                                    onClick={() => setActiveTab('media')}
                                >
                                    Recipient's Media
                                </button>
                            </div>
                        </div>

                        {activeTab === 'profile' ? renderProfileTab() : renderMediaTab()}
                    </div>
                </main>




                {deleteMedia && (
                    <div className='t-20'>
                        <DeleteConfirmationModal
                            isOpen={deleteMedia}
                            onClose={() => setDeleteMedia(false)}
                            onConfirm={() => handleDeleteConfirm()}
                            recipientName={`${recipient.first_name} ${recipient.last_name} `}
                        />
                    </div>

                )}

                {selectedMedia && (
                    <div className='t-20'>
                        <MediaModal
                            media={selectedMedia}
                            onClose={() => setSelectedMedia(null)}
                        />
                    </div>

                )}
            </div>

            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover />
        </div>
    );
};

export default RecipientDetails;